/**
 * @fileoverview Botão "Ir para as mais recentes" da conversa do Chat Interno.
 * Aparece quando a pessoa está longe do fim ou vendo um trecho antigo (aberto
 * pela busca) e mostra quantas mensagens chegaram nesse meio tempo. Ouve a
 * rolagem por conta própria: só ele é redesenhado, não a página.
 */

import React, { memo, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import { ArrowDown } from "lucide-react";

// Distância do fim, em pixels, a partir da qual o botão aparece (ou uma tela, se ela for menor).
const FAR_FROM_END = 600;

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    // Acima das ações de hover das mensagens (10).
    button: {
      position: "absolute",
      right: 24,
      bottom: 16,
      zIndex: 11,
      width: 36,
      height: 36,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0,
      borderRadius: "50%",
      border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
      background: dark ? "#18181B" : "#ffffff",
      color: dark ? "#FAFAFA" : "#09090B",
      boxShadow: dark ? "0 4px 14px rgba(0,0,0,0.4)" : "0 4px 14px rgba(15,23,42,0.12)",
      cursor: "pointer",
      opacity: 0,
      transform: "translateY(8px)",
      pointerEvents: "none",
      transition: "opacity 0.15s, transform 0.15s, background 0.15s",
      "&:hover": { background: dark ? "#27272A" : "#f4f4f5" },
      "&:focus-visible": { outline: "none", boxShadow: `0 0 0 1px ${dark ? "#5B7CFF" : "#3D5CFD"}` },
      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      [theme.breakpoints.down("sm")]: { right: 12 },
    },
    visible: { opacity: 1, transform: "translateY(0)", pointerEvents: "auto" },
    badge: {
      position: "absolute",
      top: -6,
      right: -6,
      minWidth: 18,
      height: 18,
      padding: "0 5px",
      borderRadius: 9,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: dark ? "#5B7CFF" : "#3D5CFD",
      color: "#ffffff",
      fontSize: 10,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
    },
  };
});

/**
 * @param {object} props
 * @param {{current: HTMLElement}} props.listRef A lista de mensagens (quem rola).
 * @param {string} props.contentKey Muda quando o conteúdo da lista muda (outra conversa,
 *   mensagens a mais ou a menos): a distância do fim muda sem ninguém rolar.
 * @param {boolean} props.detached A lista mostra um trecho antigo, fora do fim da conversa.
 * @param {number} props.newCount Mensagens que chegaram enquanto estava no trecho antigo.
 * @param {() => void} props.onClick
 */
const JumpToLatestButton = ({ listRef, contentKey, detached, newCount, onClick }) => {
  const classes = useStyles();
  const [far, setFar] = useState(false);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const check = () => {
      const fromEnd = list.scrollHeight - list.scrollTop - list.clientHeight;
      setFar(fromEnd > Math.min(FAR_FROM_END, list.clientHeight));
    };
    check();
    list.addEventListener("scroll", check, { passive: true });
    return () => list.removeEventListener("scroll", check);
  }, [listRef, contentKey, detached, newCount]);

  const visible = detached || far;
  const label =
    newCount > 0
      ? `Ir para as mais recentes (${newCount} ${newCount === 1 ? "nova" : "novas"})`
      : "Ir para as mais recentes";

  return (
    <Tooltip title="Ir para as mais recentes" placement="left">
      <button
        type="button"
        className={`${classes.button} ${visible ? classes.visible : ""}`}
        onClick={onClick}
        aria-label={label}
        aria-hidden={!visible}
        tabIndex={visible ? 0 : -1}
      >
        <ArrowDown size={18} />
        {newCount > 0 && <span className={classes.badge}>{newCount > 99 ? "99+" : newCount}</span>}
      </button>
    </Tooltip>
  );
};

export default memo(JumpToLatestButton);
